import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useRealtimeStore } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import {
  connectSocket,
  disconnectSocket,
  subscribeToRooms,
  requestCurrentState,
  getSocket
} from '../services/socket';
import { usePlatoonStore } from '../store/platoonStore';
import { useTargetStore } from '../store/targetStore';
import { useCcStore } from '../store/ccStore';
import { useReportStore } from '../store/reportStore';
import { useCommsStore } from '../store/commsStore';
import { operationForTarget } from '../types/domain';
import { desktopNotify, beep } from '../lib/alerts';
import toast from 'react-hot-toast';

// A platoon's display label — callsign first, no PII.
function platoonLabel(platoon: any): string {
  return (
    String(platoon?.callsign || '').trim() ||
    String(platoon?.commanderName || '').trim() ||
    'Platoon'
  );
}

// The platoon descriptor on a realtime payload (contract §12 carries `platoon`).
function payloadPlatoon(data: any): any {
  return data?.platoon || data?.device?.platoon;
}

// SOS alert sound
const playSOSAlert = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'square';
    gainNode.gain.value = 0.3;

    oscillator.start();

    // Beep pattern
    setTimeout(() => {
      oscillator.frequency.value = 600;
    }, 200);
    setTimeout(() => {
      oscillator.frequency.value = 800;
    }, 400);
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 600);
  } catch {
    // Audio not supported
  }
};

// Notification sound
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 523.25; // C5 note
    oscillator.type = 'sine';
    gainNode.gain.value = 0.2;

    oscillator.start();

    setTimeout(() => {
      oscillator.frequency.value = 659.25; // E5 note
    }, 100);
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 200);
  } catch {
    // Audio not supported
  }
};

export function useWebSocket() {
  const queryClient = useQueryClient();
  const { accessToken, isAuthenticated } = useAuthStore();
  const {
    setConnected,
    updateLocation,
    setLocations,
    addSOSEvent,
    updateSOSEvent,
    removeSOSEvent,
    setSOSEvents,
    addAlert,
    updateAlert,
    setAlerts,
    setStats,
    addNotification,
  } = useRealtimeStore();

  const reconnectAttempts = useRef(0);

  const handleConnect = useCallback(() => {
    setConnected(true);
    reconnectAttempts.current = 0;

    // Subscribe to rooms based on the user's IDENTITY, not the view filter.
    const { user } = useAuthStore.getState();
    const isHqUser = useUnitStore.getState().isHQ(user?.unitId ?? null);

    const baseRooms = [
      'location-updates',
      'sos-alerts',
      'geofence-updates',
      'operations', // renamed from patrol-updates (contract §12.2); carries platoon:phase + operation:*
      'command', // Round 2 (contract §20): ALL target:*/execution:*/target:adjudicated/feed:event/comms:* fire here
      'alerts',
      'dashboard',
      'notifications',
    ];
    // The server (§20) dual-fires every Round-2 event to `command` (HQ) and `command:<unitId>`
    // (owning unit); unitId:null globals fire to the base room only. So:
    //  - HQ MUST always join the BARE base rooms so it keeps receiving all-unit + global traffic,
    //    regardless of any persisted unit view filter (the filter is a display concern, applied in
    //    the handlers below — NOT a subscription concern). Scoping HQ here silently blinded it.
    //  - A player-unit user joins `<room>:<their unitId>` (the server enforces this scope anyway).
    // `targets`/`feed` were never valid rooms — the server's allow-list drops them silently.
    const rooms = !isHqUser && user?.unitId
      ? baseRooms.map((r) => `${r}:${user.unitId}`)
      : baseRooms;

    subscribeToRooms(rooms);

    // Request current state
    requestCurrentState();
  }, [setConnected]);

  const handleDisconnect = useCallback((reason: string) => {
    setConnected(false);

    if (reason === 'io server disconnect') {
      // Server initiated disconnect - might need to re-auth
      toast.error('Connection lost. Please refresh the page.');
    }
  }, [setConnected]);

  const handleConnectError = useCallback((error: Error) => {
    console.error('WebSocket connection error:', error);
    reconnectAttempts.current++;

    if (reconnectAttempts.current >= 5) {
      toast.error('Unable to connect to server. Please check your connection.');
    }
  }, []);

  // Handle current state response
  const handleCurrentState = useCallback((data: any) => {
    if (data.locations) {
      const { user } = useAuthStore.getState();
      const { getEffectiveUnitIds, isHQ } = useUnitStore.getState();
      const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
      const isHqUser = isHQ(user?.unitId ?? null);

      const normalized = data.locations.map((loc: any) => {
        const platoon = loc.platoon || loc.device?.platoon;
        return {
          ...loc,
          unitId: loc.unitId || platoon?.unitId,
          platoonId: loc.platoonId || platoon?.id,
          platoon,
          device: {
            ...(loc.device || {}),
            id: (loc.device?.id || loc.deviceId || '') as string,
            platoon,
          },
        };
      });

      // Pure unit-scope filter (no FS/PTL/PC taxonomy). Self-scope applies to
      // NON-HQ users only — an HQ-unit-bound commander sees all platoons.
      const filtered = normalized.filter((loc: any) => {
        const unitId = loc.unitId || loc.platoon?.unitId;
        if (effectiveUnitIds) return unitId ? effectiveUnitIds.includes(unitId) : false;
        if (user?.unitId && !isHqUser) return unitId === user.unitId;
        return true; // HQ viewing all
      });

      setLocations(filtered);
    }
    // Always set SOS events (even empty array clears old ones)
    if (Array.isArray(data?.sosEvents)) {
      setSOSEvents(data.sosEvents.filter((e: any) => e && typeof e.id === 'string' && e.status));
    } else {
      setSOSEvents([]);
    }
    if (data.alerts) {
      setAlerts(data.alerts);
    }
    if (data.stats) {
      setStats(data.stats);
    }
  }, [setLocations, setSOSEvents, setAlerts, setStats]);

  // Handle location updates
  const handleLocationUpdate = useCallback((data: any) => {
    const platoon = payloadPlatoon(data);
    const unitId = data.unitId || platoon?.unitId;

    // Pure unit-scope filter (no FS/PTL/PC taxonomy). Self-scope applies to
    // NON-HQ users only — an HQ-unit-bound commander sees all platoons.
    const { user } = useAuthStore.getState();
    const { getEffectiveUnitIds, isHQ } = useUnitStore.getState();
    const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
    const isHqUser = isHQ(user?.unitId ?? null);
    if (effectiveUnitIds) {
      if (!unitId || !effectiveUnitIds.includes(unitId)) return;
    } else if (user?.unitId && !isHqUser && unitId && unitId !== user.unitId) {
      return;
    }

    const deviceId = data.deviceId ?? data.location?.deviceId;
    const isNewTracking = !useRealtimeStore.getState().locations.get(deviceId);

    updateLocation({
      ...data.location,
      deviceId,
      platoonId: data.platoonId || platoon?.id,
      unitId,
      platoon,
      device: { id: deviceId || '', platoon },
    });

    if (isNewTracking && platoon) {
      toast.success(`✓ ${platoonLabel(platoon)} is now tracking`, {
        duration: 4000,
        style: { background: '#22c55e', color: '#fff' },
      });
    }
  }, [updateLocation]);

  // Handle batch location updates (from offline sync)
  const handleLocationBatch = useCallback((data: any) => {
    if (data.latest) {
      const platoon = payloadPlatoon(data);
      const deviceId = data.deviceId ?? data.latest?.deviceId;
      updateLocation({
        ...data.latest,
        deviceId,
        platoonId: data.platoonId || platoon?.id,
        unitId: data.unitId || platoon?.unitId,
        platoon,
        device: { id: deviceId || '', platoon },
      });
    }
  }, [updateLocation]);

  // Handle tracking stopped (platoon phase → RECOVERED, tracking cleared)
  const handleTrackingStopped = useCallback((data: any) => {
    const platoon = payloadPlatoon(data);

    // Unit-scope guard (prevents cross-unit toasts). Self-scope applies to
    // NON-HQ users only — an HQ-unit-bound commander sees all platoons.
    const { user } = useAuthStore.getState();
    const { getEffectiveUnitIds, isHQ } = useUnitStore.getState();
    const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
    const isHqUser = isHQ(user?.unitId ?? null);
    const eventUnitId = data?.unitId || platoon?.unitId || platoon?.unit?.id || null;

    if (effectiveUnitIds && eventUnitId && !effectiveUnitIds.includes(eventUnitId)) return;
    if (!effectiveUnitIds && user?.unitId && !isHqUser && eventUnitId && eventUnitId !== user.unitId) return;

    if (data.deviceId) {
      useRealtimeStore.getState().removeLocation(data.deviceId);
    }

    toast.success(`✓ ${platoonLabel(platoon)} tracking stopped`, {
      duration: 4000,
      style: { background: '#22c55e', color: '#fff' },
    });

    queryClient.invalidateQueries({ queryKey: ['platoons'] });
  }, [queryClient]);

  // Handle platoon status changes
  const handlePlatoonStatusChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['platoons'] });
  }, [queryClient]);

  // Handle force platoon update (immediate status notification)
  const handlePlatoonForceUpdate = useCallback((data: any) => {
    try {
      const platoon = payloadPlatoon(data);
      const { user } = useAuthStore.getState();
      const { getEffectiveUnitIds, isHQ } = useUnitStore.getState();
      const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
      // Self-scope applies to NON-HQ users only (HQ-unit-bound commander sees all).
      const isHqUser = isHQ(user?.unitId ?? null);
      const eventUnitId = data?.unitId || platoon?.unitId || platoon?.unit?.id || null;

      if (effectiveUnitIds && eventUnitId && !effectiveUnitIds.includes(eventUnitId)) return;
      if (!effectiveUnitIds && user?.unitId && !isHqUser && eventUnitId && eventUnitId !== user.unitId) return;

      // A recovered platoon drops off the live map.
      const platoonId = data.platoonId || platoon?.id;
      if (data.phase === 'RECOVERED' && platoonId) {
        const locations = useRealtimeStore.getState().locations;
        locations.forEach((loc, deviceId) => {
          if (loc.platoonId === platoonId) {
            useRealtimeStore.getState().removeLocation(deviceId);
          }
        });
      }

      queryClient.invalidateQueries({ queryKey: ['platoons'] });
      queryClient.invalidateQueries({ queryKey: ['rotation-platoons'] });
    } catch (error) {
      console.error('Error handling platoon force update:', error);
    }
  }, [queryClient]);

  // Handle platoon updated (callsign, commander, etc.)
  const handlePlatoonUpdated = useCallback((data: any) => {
    const platoon = payloadPlatoon(data);
    queryClient.invalidateQueries({ queryKey: ['platoons'] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
    if (platoon?.id) {
      const locations = useRealtimeStore.getState().locations;
      locations.forEach((loc) => {
        if (loc.platoonId === platoon.id) {
          useRealtimeStore.getState().updateLocation({
            ...loc,
            platoon,
            device: {
              ...loc.device,
              id: (loc.device?.id || loc.deviceId || '') as string,
              platoon,
            },
          });
        }
      });
    }
  }, [queryClient]);

  // Handle geofence updated
  const handleGeofenceUpdated = useCallback((data: any) => {
    if (data.geofence) {
      // Invalidate geofence queries to refresh everywhere
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      queryClient.invalidateQueries({ queryKey: ['geofences', data.geofence.id] });
      
      toast.success(
        `✓ Geofence updated: ${data.geofence.name}`,
        { duration: 2000 }
      );
    }
  }, [queryClient]);

  // Handle sync all event
  const handleSyncAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['platoons'] });
    queryClient.invalidateQueries({ queryKey: ['geofences'] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
    queryClient.invalidateQueries({ queryKey: ['operations'] });
  }, [queryClient]);

  // Handle SOS events
  const handleSOSEmergency = useCallback((data: any) => {
    try {
      const platoon = payloadPlatoon(data);
      playSOSAlert();

      toast.error(`🚨 SOS ALERT: ${platoonLabel(platoon)}`, {
        duration: 10000,
        style: { background: '#ff4444', color: '#fff', fontWeight: 'bold' },
      });

      addSOSEvent({
        id: data.id,
        latitude: data.latitude,
        longitude: data.longitude,
        triggerType: data.triggerType,
        status: 'TRIGGERED',
        triggeredAt: data.triggeredAt,
        unitId: data.unitId || platoon?.unitId,
        platoonId: data.platoonId || platoon?.id,
        platoon,
      });
    } catch (error) {
      console.error('Error handling SOS emergency:', error);
    }
  }, [addSOSEvent]);

  const handleSOSTriggered = useCallback((data: any) => {
    try {
      // Accept both shapes:
      // - { event: {...} } (legacy)
      // - { id, latitude, ... } (current backend)
      const event = data?.event ?? data;
      if (!event || !event.id) return;
      addSOSEvent(event);
    } catch (error) {
      console.error('Error handling SOS triggered:', error);
      // Try to add with validation
      const event = data?.event ?? data;
      if (event && event.id) {
        try {
          addSOSEvent({
            id: event.id,
            latitude: event.latitude || 0,
            longitude: event.longitude || 0,
            triggerType: event.triggerType || 'BUTTON',
            status: event.status || 'TRIGGERED',
            triggeredAt: event.triggeredAt || new Date().toISOString(),
            platoon: event.platoon,
            platoonId: event.platoonId,
            unitId: event.unitId,
          });
        } catch (fallbackError) {
          console.error('Fallback SOS triggered add failed:', fallbackError);
        }
      }
    }
  }, [addSOSEvent]);

  const handleSOSAcknowledged = useCallback((data: any) => {
    // Accept both shapes:
    // - { event: { id, acknowledgedAt, ... } }
    // - { sosId, status, unitId }
    const event = data?.event;
    const sosId = event?.id ?? data?.sosId;
    if (!sosId) return;

    updateSOSEvent(sosId, {
      status: 'ACKNOWLEDGED',
      acknowledgedAt: event?.acknowledgedAt,
      acknowledgedBy: event?.acknowledgedBy,
    });

    toast.success(`SOS acknowledged in ${Math.round(data.timeToAckMs / 1000)}s`);
  }, [updateSOSEvent]);

  // sos:responder → { sosId, status, unitId, responder } (docs/19 §3.6). Merge the
  // responder into the SOS event and reflect the RESPONDING status.
  const handleSOSResponder = useCallback((data: any) => {
    const sosId = data?.sosId ?? data?.event?.id;
    const responder = data?.responder;
    if (!sosId || !responder?.id) return;
    const current = useRealtimeStore.getState().sosEvents.find((e: any) => e.id === sosId);
    const existing = (current?.responders ?? []) as any[];
    const merged = existing.some((r) => r.id === responder.id)
      ? existing.map((r) => (r.id === responder.id ? { ...r, ...responder } : r))
      : [...existing, responder];
    updateSOSEvent(sosId, { status: data.status ?? current?.status, responders: merged } as any);
  }, [updateSOSEvent]);

  const handleSOSResolved = useCallback((data: any) => {
    // Accept both shapes: { sosId } or { event: { id } }
    const sosId = data?.sosId ?? data?.event?.id;
    if (!sosId) return;
    // Remove resolved SOS from the list entirely
    removeSOSEvent(sosId);
    toast.success('SOS event resolved');
  }, [removeSOSEvent]);

  const handleSOSCancelled = useCallback((data: any) => {
    const sosId = data?.sosId ?? data?.event?.id;
    if (!sosId) return;
    removeSOSEvent(sosId);
    toast('SOS event cancelled', { icon: 'ℹ️' });
  }, [removeSOSEvent]);

  // Handle alerts
  const handleNewAlert = useCallback((data: any) => {
    // Accept both shapes:
    // - { alert: {...} } (new)
    // - { id, type, ... } (legacy)
    const alert = data?.alert || data;
    if (!alert || !alert.id) {
      console.warn('handleNewAlert: invalid alert payload', data);
      return;
    }
    
    addAlert(alert);

    const severityColors: Record<string, string> = {
      CRITICAL: '#ff2d55',
      HIGH: '#ff6b00',
      MEDIUM: '#ffcc00',
      LOW: '#3b82f6',
      INFO: '#5ac8fa',
    };

    toast(alert.title || 'New Alert', {
      icon: alert.severity === 'CRITICAL' ? '🚨' : '⚠️',
      style: {
        borderLeft: `4px solid ${severityColors[alert.severity] || '#5ac8fa'}`,
      },
    });
  }, [addAlert]);

  const handleAlertUpdated = useCallback((data: any) => {
    updateAlert(data.alert.id, data.alert);
  }, [updateAlert]);

  const handleCriticalAlert = useCallback((data: any) => {
    playSOSAlert();

    const title = data?.title || data?.alert?.title || 'CRITICAL ALERT';
    toast.error(`🚨 CRITICAL: ${title}`, {
      duration: 10000,
      style: {
        background: '#ff2d55',
        color: '#fff',
        fontWeight: 'bold',
      },
    });
  }, []);

  // Handle notifications (messages from app)
  const handleNewNotification = useCallback((data: any) => {

    // Play notification sound
    playNotificationSound();

    // Add to notifications store
    addNotification({
      id: data.id,
      type: data.type || 'USER_MESSAGE',
      title: data.title,
      message: data.message,
      senderName: data.senderName,
      senderEmail: data.senderEmail,
      timestamp: new Date(data.timestamp),
      read: false,
    });

    // Show toast notification
    toast(`💬 ${data.title}: ${data.message.substring(0, 50)}${data.message.length > 50 ? '...' : ''}`, {
      duration: 5000,
      style: {
        background: '#1f2937',
        color: '#fff',
        borderLeft: '4px solid #3b82f6',
      },
    });
  }, [addNotification]);

  // Handle manual phase tag broadcast (contract §12: platoon:phase)
  const handlePlatoonPhase = useCallback((data: any) => {
    const platoonId = data?.platoonId;
    if (!platoonId || !data?.phase) return;
    usePlatoonStore.getState().applyPhase(platoonId, data.phase, data.operationId ?? undefined);

    // Reflect the phase on any live marker for this platoon.
    const locations = useRealtimeStore.getState().locations;
    locations.forEach((loc) => {
      if (loc.platoonId === platoonId && loc.platoon) {
        useRealtimeStore.getState().updateLocation({
          ...loc,
          platoon: { ...loc.platoon, currentPhase: data.phase },
        });
      }
    });

    queryClient.invalidateQueries({ queryKey: ['platoons'] });
    queryClient.invalidateQueries({ queryKey: ['rotation-platoons'] });
    queryClient.invalidateQueries({ queryKey: ['operations'] });
    if (data.operationId) queryClient.invalidateQueries({ queryKey: ['operation', data.operationId] });
  }, [queryClient]);

  // Handle field-dropped live hideout pin (contract §12: geofence:hideout)
  const handleGeofenceHideout = useCallback((data: any) => {
    queryClient.invalidateQueries({ queryKey: ['geofences'] });
    toast(`📍 Hideout dropped: ${data?.geofence?.name || 'Hideout'}`, { duration: 4000, icon: '📍' });
  }, [queryClient]);

  // ---- Round 2: Command Center events (contract §20, base room `command`) ----

  // target:created / target:updated → { target } (full DTO). Upsert + refresh the
  // Location Studio / target board / disposition so every session sees it live.
  const handleTargetCreated = useCallback((data: any) => {
    const t = data?.target || data;
    if (t?.id) useTargetStore.getState().upsertTarget(t);
    queryClient.invalidateQueries({ queryKey: ['targets'] });
    queryClient.invalidateQueries({ queryKey: ['force-disposition'] });
    queryClient.invalidateQueries({ queryKey: ['scoreboard'] });
  }, [queryClient]);

  // target:deleted → { targetId, unitId }
  const handleTargetDeleted = useCallback((data: any) => {
    const targetId = data?.targetId || data?.target?.id || data?.id;
    if (targetId) {
      useTargetStore.getState().removeTarget(targetId);
      useCcStore.getState().stopExecution(targetId);
    }
    queryClient.invalidateQueries({ queryKey: ['targets'] });
    queryClient.invalidateQueries({ queryKey: ['force-disposition'] });
    queryClient.invalidateQueries({ queryKey: ['scoreboard'] });
  }, [queryClient]);

  const handleTargetStatus = useCallback((data: any) => {
    const t = data?.target || data;
    if (t?.id) {
      if (t.name) useTargetStore.getState().upsertTarget(t);
      else if (t.status) useTargetStore.getState().applyStatus(t.id, t.status);
    }
    queryClient.invalidateQueries({ queryKey: ['targets'] });
    queryClient.invalidateQueries({ queryKey: ['scoreboard'] });
  }, [queryClient]);

  const handleTargetAssigned = useCallback((data: any) => {
    const t = data?.target || data;
    if (t?.id && t.name) useTargetStore.getState().upsertTarget(t);
    else if (t?.id) useTargetStore.getState().applyStatus(t.id, 'ASSIGNED');
    queryClient.invalidateQueries({ queryKey: ['targets'] });
  }, [queryClient]);

  // target:proximity → { targetId, platoonId, callsign, unitId, distanceMeters } (§20 — no nested target).
  const handleTargetProximity = useCallback((data: any) => {
    const targetId = data?.targetId || data?.target?.id;
    const platoonId = data?.platoonId || data?.platoon?.id;
    const callsign = data?.callsign || data?.platoon?.callsign || 'Platoon';
    // The payload carries no target name; resolve it from the store.
    const name = (targetId && useTargetStore.getState().getById(targetId)?.name) || data?.target?.name || 'target';
    const dist = typeof data?.distanceMeters === 'number' ? ` (${Math.round(data.distanceMeters)} m)` : '';
    if (targetId && platoonId) {
      useCcStore.getState().setProximity({ targetId, platoonId, at: new Date().toISOString() });
    }
    beep('proximity');
    desktopNotify('Proximity 200 m', `${callsign} is within 200 m of ${name}${dist}`);
    toast(`🎯 ${callsign} within 200 m of ${name}${dist}`, { duration: 6000, icon: '🎯' });
    queryClient.invalidateQueries({ queryKey: ['targets'] });
  }, [queryClient]);

  const handleExecutionStart = useCallback((data: any) => {
    const t = data?.target || data;
    const targetId = t?.id || data?.targetId;
    if (!targetId) return;
    const opType =
      data?.operationType ||
      data?.operation?.type ||
      (t?.type ? operationForTarget(t.type) : 'RAID');
    useCcStore.getState().startExecution({
      targetId,
      operationType: opType,
      platoonId: data?.platoonId || data?.platoon?.id,
      callsign: data?.platoon?.callsign || data?.callsign,
      startedAt: new Date().toISOString(),
    });
    useTargetStore.getState().applyStatus(targetId, 'EXECUTING');
    beep('alert');
    toast(`⚔️ Execution started: ${t?.name || 'target'}`, { duration: 5000, icon: '⚔️' });
    queryClient.invalidateQueries({ queryKey: ['targets'] });
  }, [queryClient]);

  const handleExecutionStop = useCallback((data: any) => {
    const targetId = data?.target?.id || data?.targetId || data?.id;
    if (targetId) useCcStore.getState().stopExecution(targetId);
    queryClient.invalidateQueries({ queryKey: ['targets'] });
  }, [queryClient]);

  // target:adjudicated → { targetId, success, status, unitId } (§20 — status is ELIMINATED / ASSIGNED / FAILED).
  const handleTargetAdjudicated = useCallback((data: any) => {
    const targetId = data?.targetId || data?.target?.id;
    const status = data?.status || data?.target?.status;
    if (targetId) {
      useCcStore.getState().stopExecution(targetId);
      if (status) useTargetStore.getState().applyStatus(targetId, status);
    }
    queryClient.invalidateQueries({ queryKey: ['targets'] });
    queryClient.invalidateQueries({ queryKey: ['scoreboard'] });
    queryClient.invalidateQueries({ queryKey: ['force-disposition'] });
  }, [queryClient]);

  const handleFeedEvent = useCallback((data: any) => {
    const ev = data?.event || data;
    // Drop malformed events — one bad payload (missing id or unparseable timestamp) must not
    // reach the feed, where date-fns would throw and the app-wide ErrorBoundary would blank the UI.
    const tsOk = !!ev?.timestamp && !Number.isNaN(new Date(ev.timestamp).getTime());
    if (ev?.id && tsOk) useCcStore.getState().pushFeed(ev);
    queryClient.invalidateQueries({ queryKey: ['feed'] });
  }, [queryClient]);

  const handleCommsLost = useCallback((data: any) => {
    const platoonId = data?.platoonId || data?.platoon?.id;
    if (platoonId) useCcStore.getState().setCommsDark(platoonId, true);
    queryClient.invalidateQueries({ queryKey: ['comms-status'] });
  }, [queryClient]);

  const handleCommsRestored = useCallback((data: any) => {
    const platoonId = data?.platoonId || data?.platoon?.id;
    if (platoonId) useCcStore.getState().setCommsDark(platoonId, false);
    queryClient.invalidateQueries({ queryKey: ['comms-status'] });
  }, [queryClient]);

  // ---- P1: Field reports (docs/19 §1) — report:new / report:acked ----

  // report:new → { report } (full DTO). Push to the live buffer (drives the ACK
  // siren) and beep once on arrival for the ack-demanding tiers.
  const handleReportNew = useCallback((data: any) => {
    const report = data?.report || data;
    if (!report?.id) return;
    useReportStore.getState().pushReport(report);
    if (report.ackRequired && (report.priority === 'FLASH' || report.priority === 'IMMEDIATE')) {
      beep('alert');
      desktopNotify(`${report.priority} ${report.type}`, `${report.platoon?.callsign ?? 'Field'} — acknowledgement required`);
    }
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [queryClient]);

  // report:acked → { report } (full DTO). Reconcile the live buffer so the siren
  // clears the moment HQ acknowledges anywhere.
  const handleReportAcked = useCallback((data: any) => {
    const report = data?.report || data;
    if (!report?.id) return;
    useReportStore.getState().applyAck(report);
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [queryClient]);

  // ---- P2: Comms — message:new / order:new / order:acked (docs/19 §3.2/§3.3) ----

  const handleMessageNew = useCallback((data: any) => {
    const message = data?.message || data;
    if (!message?.id) return;
    useCommsStore.getState().pushMessage(message);
    queryClient.invalidateQueries({ queryKey: ['message-threads'] });
    queryClient.invalidateQueries({ queryKey: ['messages', message.platoonId] });
  }, [queryClient]);

  const handleOrderNew = useCallback((data: any) => {
    const order = data?.order || data;
    if (!order?.id) return;
    useCommsStore.getState().pushOrder(order);
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  }, [queryClient]);

  const handleOrderAcked = useCallback((data: any) => {
    const order = data?.order || data;
    if (!order?.id) return;
    useCommsStore.getState().applyOrderAck(order);
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    // Plan-linked FragO ack advances the plan ack pipeline (docs/19 §3.4).
    if (order.planId) queryClient.invalidateQueries({ queryKey: ['plans'] });
  }, [queryClient]);

  // ---- P3: Operation plans — plan:issued / plan:advance (docs/19 §3.4) ----

  const handlePlanIssued = useCallback((data: any) => {
    const plan = data?.plan || data;
    queryClient.invalidateQueries({ queryKey: ['plans'] });
    if (plan?.id) queryClient.invalidateQueries({ queryKey: ['plan', plan.id] });
  }, [queryClient]);

  const handlePlanAdvance = useCallback((data: any) => {
    const planId = data?.planId || data?.plan?.id;
    queryClient.invalidateQueries({ queryKey: ['plans'] });
    if (planId) queryClient.invalidateQueries({ queryKey: ['plan', planId] });
  }, [queryClient]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      disconnectSocket();
      return;
    }

    const socket = connectSocket(accessToken);

    // Connection events
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    // State events
    socket.on('state:current', handleCurrentState);

    // Location events
    socket.on('location:update', handleLocationUpdate);
    socket.on('location:batch', handleLocationBatch);
    socket.on('tracking:stopped', handleTrackingStopped);

    // Platoon events (contract §12)
    socket.on('platoon:phase', handlePlatoonPhase);
    socket.on('platoon:status-changed', handlePlatoonStatusChanged);
    socket.on('platoon:force-update', handlePlatoonForceUpdate);
    socket.on('platoon:updated', handlePlatoonUpdated);

    // Geofence events
    socket.on('geofence:updated', handleGeofenceUpdated);
    socket.on('geofence:hideout', handleGeofenceHideout);

    // Operation events
    socket.on('operation:created', handleSyncAll);
    socket.on('operation:updated', handleSyncAll);

    // Sync events
    socket.on('sync:all', handleSyncAll);
    socket.on('sync:geofences', handleGeofenceUpdated);

    // SOS events
    socket.on('sos:emergency', handleSOSEmergency);
    socket.on('sos:triggered', handleSOSTriggered);
    socket.on('sos:acknowledged', handleSOSAcknowledged);
    socket.on('sos:resolved', handleSOSResolved);
    socket.on('sos:cancelled', handleSOSCancelled);
    socket.on('sos:responder', handleSOSResponder);

    // Alert events
    socket.on('alert:new', handleNewAlert);
    socket.on('alert:updated', handleAlertUpdated);
    socket.on('alert:critical', handleCriticalAlert);

    // Notification events (messages from app)
    socket.on('notification:new', handleNewNotification);

    // Round 2: Command Center events (contract §20)
    socket.on('target:created', handleTargetCreated);
    socket.on('target:updated', handleTargetCreated);
    socket.on('target:deleted', handleTargetDeleted);
    socket.on('target:status', handleTargetStatus);
    socket.on('target:assigned', handleTargetAssigned);
    socket.on('target:proximity', handleTargetProximity);
    socket.on('execution:start', handleExecutionStart);
    socket.on('execution:stop', handleExecutionStop);
    socket.on('target:adjudicated', handleTargetAdjudicated);
    socket.on('feed:event', handleFeedEvent);
    socket.on('comms:lost', handleCommsLost);
    socket.on('comms:restored', handleCommsRestored);

    // P1: Field reports
    socket.on('report:new', handleReportNew);
    socket.on('report:acked', handleReportAcked);

    // P2: Comms (messages + orders)
    socket.on('message:new', handleMessageNew);
    socket.on('order:new', handleOrderNew);
    socket.on('order:acked', handleOrderAcked);

    // P3: Operation plans
    socket.on('plan:issued', handlePlanIssued);
    socket.on('plan:advance', handlePlanAdvance);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('state:current', handleCurrentState);
      socket.off('location:update', handleLocationUpdate);
      socket.off('location:batch', handleLocationBatch);
      socket.off('tracking:stopped', handleTrackingStopped);
      socket.off('platoon:phase', handlePlatoonPhase);
      socket.off('platoon:status-changed', handlePlatoonStatusChanged);
      socket.off('platoon:force-update', handlePlatoonForceUpdate);
      socket.off('platoon:updated', handlePlatoonUpdated);
      socket.off('geofence:updated', handleGeofenceUpdated);
      socket.off('geofence:hideout', handleGeofenceHideout);
      socket.off('operation:created', handleSyncAll);
      socket.off('operation:updated', handleSyncAll);
      socket.off('sync:all', handleSyncAll);
      socket.off('sync:geofences', handleGeofenceUpdated);
      socket.off('sos:emergency', handleSOSEmergency);
      socket.off('sos:triggered', handleSOSTriggered);
      socket.off('sos:acknowledged', handleSOSAcknowledged);
      socket.off('sos:resolved', handleSOSResolved);
      socket.off('sos:cancelled', handleSOSCancelled);
      socket.off('sos:responder', handleSOSResponder);
      socket.off('alert:new', handleNewAlert);
      socket.off('alert:updated', handleAlertUpdated);
      socket.off('alert:critical', handleCriticalAlert);
      socket.off('notification:new', handleNewNotification);
      socket.off('target:created', handleTargetCreated);
      socket.off('target:updated', handleTargetCreated);
      socket.off('target:deleted', handleTargetDeleted);
      socket.off('target:status', handleTargetStatus);
      socket.off('target:assigned', handleTargetAssigned);
      socket.off('target:proximity', handleTargetProximity);
      socket.off('execution:start', handleExecutionStart);
      socket.off('execution:stop', handleExecutionStop);
      socket.off('target:adjudicated', handleTargetAdjudicated);
      socket.off('feed:event', handleFeedEvent);
      socket.off('comms:lost', handleCommsLost);
      socket.off('comms:restored', handleCommsRestored);
      socket.off('report:new', handleReportNew);
      socket.off('report:acked', handleReportAcked);
      socket.off('message:new', handleMessageNew);
      socket.off('order:new', handleOrderNew);
      socket.off('order:acked', handleOrderAcked);
      socket.off('plan:issued', handlePlanIssued);
      socket.off('plan:advance', handlePlanAdvance);

      disconnectSocket();
    };
  }, [
    isAuthenticated,
    accessToken,
    handleConnect,
    handleDisconnect,
    handleConnectError,
    handleCurrentState,
    handleLocationUpdate,
    handleLocationBatch,
    handleTrackingStopped,
    handlePlatoonPhase,
    handlePlatoonStatusChanged,
    handlePlatoonForceUpdate,
    handlePlatoonUpdated,
    handleGeofenceUpdated,
    handleGeofenceHideout,
    handleSyncAll,
    handleSOSEmergency,
    handleSOSTriggered,
    handleSOSAcknowledged,
    handleSOSResolved,
    handleSOSCancelled,
    handleSOSResponder,
    handleNewAlert,
    handleAlertUpdated,
    handleCriticalAlert,
    handleNewNotification,
    handleTargetCreated,
    handleTargetDeleted,
    handleTargetStatus,
    handleTargetAssigned,
    handleTargetProximity,
    handleExecutionStart,
    handleExecutionStop,
    handleTargetAdjudicated,
    handleFeedEvent,
    handleCommsLost,
    handleCommsRestored,
    handleReportNew,
    handleReportAcked,
    handleMessageNew,
    handleOrderNew,
    handleOrderAcked,
    handlePlanIssued,
    handlePlanAdvance,
  ]);

  return { socket: getSocket() };
}
